import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    ToggleButton,
    Tooltip,
    Typography
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {useTranslation} from "react-i18next";
import {makeRequest, rolesState} from "../../services/requestService";
import {API_URL, COLLECTION_ROLE, USER_ROLES} from "../../constants";
import {useHistory, useLocation, useParams} from "react-router-dom";
import HomeLayout from "../../components/HomeLayout/HomeLayout";
import {useSnackbar} from "notistack";
import ImageIcon from "@mui/icons-material/Image";
import HideImageIcon from "@mui/icons-material/HideImage";
import TitleCard from "../../components/Cards/TitleCard";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CollectionDialog from "../../components/Collection/CollectionDialog";
import ImportTitleDialog from "../../components/Collection/ImportTitleDialog";
import {useRecoilValue} from "recoil";
import GroupsIcon from "@mui/icons-material/Groups";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MemberList from "../../components/Collection/MemberList";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axios from "axios";

const LoadingState = {
    UNSET: 0b000, // 0
    LOADED: 0b001,
    LOADING_COLLECTION: 0b010, // 2
    COLLECTION_LOADED: 0b011, // 3
    LOADING_TITLES: 0b110, // 6
    TITLES_LOADED: 0b111, // 7
    has(state, search) {
        return state & search !== 0
    }
}

const PAGE_SIZE = 42;

async function fetchCollection(id, publicAccess = false) {
    const url = `${API_URL}/user/collection/${id}?publicAccess=${publicAccess}`;
    const response = await makeRequest(url);

    if (response.status !== 200) {
        throw response;
    }

    return response.data;
}


async function exportCollection(collection) {
    const url = `${API_URL}/collection/${collection.id}/export`;
    const response = await axios({
        url,
        method: "GET",
        responseType: "blob",
    });

    const blobUrl = URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", `${collection.name}.zip`);
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

async function fetchData(searchValue, collectionId, page = 0) {
    let url;
    let params = `page=${page}&size=${PAGE_SIZE}&collections=${collectionId}`;
    if (searchValue) {
        url = `${API_URL}/titles/search/bycollections?query=${searchValue}&${params}`;
    } else {
        url = `${API_URL}/titles/bycollection?${params}`;
    }

    const response = await makeRequest(url);

    if (response.status === 200) {
        const data = response.data.data;
        let titles = [];
        if (Array.isArray(data)) {
            titles = data;
            if (page !== 0) {
                titles = prevState => [...prevState, ...data];
            }
        }
        return {titles, ...response.data};
    } else if (response.status === 400 || response.status > 403) {
        throw response;
    }
}

export default function Collection() {
    const {search} = useLocation();
    const {t} = useTranslation('common');
    const {id} = useParams();
    const history = useHistory();
    const [collection, setCollection] = React.useState({name: "", description: ""});
    const [role, setRole] = React.useState("READER");
    const userRoles = useRecoilValue(rolesState);
    const [hideImg, setHideImg] = React.useState(false);
    const [titles, setTitles] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [state, setState] = React.useState(LoadingState.UNSET);
    const [searchValue, setSearchValue] = React.useState("");
    const [shownDialog, setShownDialog] = React.useState("none");
    const {enqueueSnackbar} = useSnackbar();
    const [updateCollection, setUpdateCollection] = React.useState(0);
    const [downloading, setDownloading] = React.useState(false);

    const publicAccess = React.useMemo(() => new URLSearchParams(search).get('publicAccess') === 'true',
        [search]);

    console.log(search, publicAccess);

    React.useEffect(() => {
        document.title = t("main.title") + " - " + t("main.pages.collection.title");
    }, [t]);

    React.useEffect(async () => {
        setState(LoadingState.LOADING_COLLECTION)
        const {collection, role} = await fetchCollection(id, publicAccess);
        setCollection(collection);
        setRole(role);
        setState(LoadingState.LOADING_TITLES);
        await loadData(searchValue, id, 0)
    }, [id, updateCollection]);

    async function downloadCollection() {
        setDownloading(true);
        await exportCollection(collection);
        setDownloading(false);
    }

    async function loadData(searchValue, collectionId, page = 0) {
        try {
            const {titles, totalPages, currentPage} = await fetchData(searchValue, collectionId, page);
            setState(LoadingState.TITLES_LOADED);
            setTitles(titles);
            setTotalPages(totalPages);
            setCurrentPage(currentPage);
        } catch (e) {
            if (searchValue) {
                console.log(e)
                enqueueSnackbar(t("main.components.search.error"), {variant: "error"});
                return;
            }
            if (e.status === 404) {
                history.replace("/error/notfound");
            } else {
                history.push("/error");
            }
        }
    }

    const handleSubmitSearch = (search) => {
        setSearchValue(search);
    }

    async function handleDeleteCollection() {
        const url = `${API_URL}/collection/${id}`;
        const response = await makeRequest(url, "DELETE");

        if (response.status === 200) {
            history.push("/");
        }
    }

    // RENDER

    const toolbarElms = (
        <Tooltip title={hideImg ? t("main.components.button.showImg") : t("main.components.button.hideImg")}>
            <span>
                <ToggleButton
                    size="small"
                    value="hide"
                    selected={hideImg}
                    onChange={() => setHideImg(prevState => !prevState)}
                    disabled={!state}
                >
                    {hideImg
                        ? <ImageIcon sx={{color: theme => theme.palette.text.secondary}}/>
                        : <HideImageIcon sx={{color: theme => theme.palette.text.secondary}}/>}
                </ToggleButton>
            </span>
        </Tooltip>
    );

    const content = state === LoadingState.TITLES_LOADED ?
        titles.map((title) => (<TitleCard key={`card-title-${title.id}`} title={title} hideImg={hideImg}/>)) :
        [<Stack key={"loading-home"} alignItems="center" m={4}><CircularProgress size={80}/></Stack>];

    const aside = (
        <Card elevation={0}>
            <CardContent>
                <Typography color="textSecondary" display="flex" alignItems="center" gutterBottom>
                    <InfoIcon fontSize="small" sx={{mr: 1}}/>
                    {" " + t('main.pages.home.informations.title')}
                </Typography>
                <Typography variant="body2" component="p">
                    {t('main.pages.home.informations.content')}
                </Typography>
            </CardContent>
        </Card>
    );

    const title = (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
                <Typography ml={2} variant="h6" fontSize={22} fontWeight="bold" color="textSecondary" display="flex"
                            alignItems="center" gap={1}>
                    {collection.name}
                    <Tooltip
                        title={t(`main.components.form.collection.visibility.${collection.visibility?.toLowerCase()}`)}
                        arrow>
                        {collection.visibility === "PRIVATE" ? (<LockIcon/>) : <LockOpenIcon/>}
                    </Tooltip>
                </Typography>
                <Typography ml={2} variant="subtitle1">
                    {collection.description}
                </Typography>
            </Box>
            <Box>
                {([COLLECTION_ROLE.OWNER, COLLECTION_ROLE.MAINTAINER].includes(role) || userRoles.includes(USER_ROLES.ADMIN)) &&
                    <>
                        <Tooltip title={t("main.pages.collection.actions.users.tooltip")} arrow>
                            <IconButton onClick={() => setShownDialog("members")}><GroupsIcon/></IconButton>
                        </Tooltip>
                        <Tooltip title={t("main.pages.collection.actions.import.tooltip")} arrow>
                            <IconButton onClick={() => setShownDialog("import")}><FileUploadIcon/></IconButton>
                        </Tooltip>
                        <Tooltip title={t("main.pages.collection.actions.download.tooltip")} arrow>
                            <IconButton onClick={() => downloadCollection()}>
                                <FileDownloadIcon/>
                            </IconButton>
                        </Tooltip>
                    </>
                }
                {(role === COLLECTION_ROLE.OWNER || userRoles.includes(USER_ROLES.ADMIN)) &&
                    <>
                        <Tooltip title={t("main.pages.collection.actions.edit.tooltip")} arrow>
                            <IconButton onClick={() => setShownDialog("edit_collection")}><EditIcon/></IconButton>
                        </Tooltip>
                        <Tooltip title={t("main.pages.collection.actions.delete.tooltip")} arrow>
                            <IconButton onClick={() => setShownDialog("confirm")}><DeleteIcon
                                color="error"/></IconButton>
                        </Tooltip>
                    </>
                }
            </Box>
        </Stack>
    )

    return (
        <>
            <HomeLayout
                titleElement={title}
                content={content}
                aside={aside}
                tabs={<></>}
                toolbarElms={toolbarElms}
                onScrollHitBottom={() => loadData(searchValue, id, currentPage + 1)}
                onSubmit={handleSubmitSearch}
                noDataText={t("main.pages.collection.titles.empty")}
                hasMoreData={state === LoadingState.TITLES_LOADED ? currentPage + 1 < totalPages : false}
            />
            <CollectionDialog
                open={shownDialog === "edit_collection"}
                mode="EDIT"
                collection={collection}
                onClose={() => setShownDialog("none")}
                onCreated={() => {
                    setUpdateCollection(updateCollection + 1);
                    setShownDialog("none");
                }}
            />
            <Dialog open={shownDialog === "members"} onClose={() => setShownDialog("none")}>
                <DialogTitle>
                    {t("main.pages.collection.actions.users.tooltip")}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => setShownDialog("none")}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon/>
                </IconButton>
                <DialogContent>
                    <MemberList collection={id}/>
                </DialogContent>
            </Dialog>
            <ImportTitleDialog
                open={shownDialog === "import"}
                collection={id}
                onClose={() => setShownDialog("none")}
                onUploaded={async (title) => {
                    setShownDialog("none");
                    await loadData(searchValue, id, currentPage);
                }}
            />
            <Dialog
                open={shownDialog === "confirm"}
                onClose={() => setShownDialog("none")}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("main.pages.collection.actions.delete.confirmation.title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("main.pages.collection.actions.delete.confirmation.description")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShownDialog("none")}>
                        {t("main.pages.collection.actions.delete.confirmation.cancel")}
                    </Button>
                    <Button onClick={() => handleDeleteCollection()} color="error">
                        {t("main.pages.collection.actions.delete.confirmation.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={downloading} fullWidth>
                <DialogTitle>
                    {t("main.pages.collection.downloading")}
                </DialogTitle>
                <DialogContent>
                    <Box textAlign="center" py={4}>
                        <CircularProgress size={80}/>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
